import i18n from "../i18n";
import { IInboxConversation } from "../models/messagesInbox/IInboxConversation";
import { PartStatus } from "../models/Part";

const tr = (key: string, options?: Record<string, unknown>) => i18n.t(key, { ns: "messages", ...options });

/** Which link it is, so code can find one without matching its (translated) label. */
export type InboxLinkId = "order" | "part" | "donor_stock" | "fits_reg" | "listing";

export interface IInboxLink {
  id: InboxLinkId;
  label: string;
  /** Where it opens (new tab). Missing when we can't link yet; `why` says what's missing. */
  href?: string;
  why?: string;
}

// eBay order numbers look like 12-11234-56789; Seller Hub opens them by that number.
const EBAY_ORDER_NUMBER = /^\d{2}-\d{5}-\d{5}$/;

/** Part page in Wilma (the same page a scanned tag opens). */
export function partHref(c: IInboxConversation): string | undefined {
  return c.part?.id ? `/stock/${c.part.id}` : undefined;
}

/** Donor vehicle page in Wilma. */
export function donorHref(c: IInboxConversation): string | undefined {
  return c.donor?.id ? `/vehicles/${c.donor.id}` : undefined;
}

const compactReg = (reg: string) => reg.replace(/\s+/g, "").toUpperCase();

/**
 * Parts off the donor that we can still sell: the Stock page (unsold only) searched by the donor's
 * reg, status Dismantled – so not still on the car, missing, damaged, in QC or returned.
 */
export function donorStockHref(c: IInboxConversation): string | undefined {
  const vrm = c.donor?.vrm ? compactReg(c.donor.vrm) : "";
  return vrm ? `/stock?search=${encodeURIComponent(vrm)}&status=${PartStatus.Dismantled}` : undefined;
}

/** Parts on the shelf that fit the buyer's car (the Stock page's Smart Match). */
export function fitsRegStockHref(reg: string | undefined): string | undefined {
  const r = reg ? compactReg(reg) : "";
  return r ? `/stock?fits=${encodeURIComponent(r)}&status=${PartStatus.Dismantled}` : undefined;
}

/** The buyer's reg as far as we know it: the one checked or typed in the panel, else the order's. */
export function buyerReg(c: IInboxConversation, typedReg?: string): string | undefined {
  return typedReg || c.fitment?.reg || c.order?.buyerVehicleReg || undefined;
}

/** The links under the right-hand panel, in the order they show. */
export function inboxLinks(c: IInboxConversation, typedReg?: string): IInboxLink[] {
  const channelName = c.channel === "ebay" ? "eBay" : "PartPro";
  const links: IInboxLink[] = [];

  if (c.order) {
    if (c.channel === "ebay" && EBAY_ORDER_NUMBER.test(c.order.orderNumber)) {
      links.push({ id: "order", label: tr("Order on eBay"), href: `https://www.ebay.co.uk/sh/ord/details?orderid=${c.order.orderNumber}` });
    } else {
      links.push({ id: "order", label: tr("Order on {{channel}}", { channel: channelName }), why: c.channel === "ebay" ? tr("Not an eBay order number") : tr("PartPro has no order link Wilma knows yet") });
    }
  }

  if (c.part) {
    const label = tr("Part in Wilma");
    links.push(partHref(c) ? { id: "part", label, href: partHref(c) } : { id: "part", label, why: tr("This listing isn't linked to a part in Wilma") });
  }
  if (c.donor) {
    const label = tr("In stock from this car");
    links.push(donorStockHref(c) ? { id: "donor_stock", label, href: donorStockHref(c) } : { id: "donor_stock", label, why: tr("The donor has no reg in Wilma") });
  }
  const reg = buyerReg(c, typedReg);
  links.push(
    reg
      ? { id: "fits_reg", label: tr("Parts that fit {{reg}}", { reg: compactReg(reg) }), href: fitsRegStockHref(reg) }
      : { id: "fits_reg", label: tr("Parts that fit the buyer's car"), why: tr("Needs the buyer's reg or VIN") }
  );

  if (c.part) {
    if (c.channel === "ebay") {
      const label = tr("Listing on eBay");
      links.push(c.part.listingUrl ? { id: "listing", label, href: c.part.listingUrl } : { id: "listing", label, why: tr("No eBay item number on this conversation") });
    } else {
      links.push({ id: "listing", label: tr("Listing on PartPro"), why: tr("PartPro has no listing link Wilma knows yet") });
    }
  }

  return links;
}
